// biome-ignore lint/style/useNodejsImportProtocol: we use the events module, but biome can't detect it in a monorepo yet
import { EventEmitter } from 'events';
import * as Y from 'yjs';

import { SCHEMAS } from '@gitbook/doc-core';
import {
    checkYDocument,
    type DataSyncRPCService,
    type DataSyncRPCServiceName,
    documentChangeToY,
    getYSlateDocument,
    type IAICompletionReply,
    type IAICompletionRequest,
    normalizeYDocument,
    selectionToYSelection,
    YDocumentCheckFail,
    ySelectionToSelection,
    yToDocument,
} from '@gitbook/hive-core';
import {
    type Change,
    Data,
    type Document,
    type SelectionRange,
    serialize,
    TextRange,
    Value,
} from '@gitbook/slate';
import { Logger, ReportException, SentryTags, startStopwatch } from '@gitbook/util-debug';
import { List } from '@gitbook/util-immutable';
import { filterOutNullable } from '@gitbook/util-ts';

import type { HiveDataSyncBrowserCache } from './HiveDataSyncBrowserCache';
import {
    HiveDataSyncClient,
    HiveDataSyncState,
    HiveDataSyncUpdateOrigin,
    type HiveGlobalPresence,
} from './HiveDataSyncClient';
import type { HiveDataSyncManager } from './HiveDataSyncManager';

export type HiveDocumentPresence = HiveGlobalPresence;

export interface HiveDocumentSelectionData {
    clientId: string;
}

export interface HiveWorkingDocumentOptions {
    /**
     * When user is editing alone, we batch updates to avoid sending too many messages
     * to the server.
     * When editing with other users, we don't batch to ensure a perception of realtime collaboration.
     *
     * Wait time before sending update to the server when editing alone.
     * @default 1500 ms
     */
    singleEditBatchingInterval?: number;

    /**
     * Is the datasync client opened in read-only mode
     * @default false
     */
    readOnly?: boolean;

    /**
     * A cache to use with the datasync.
     */
    cache?: HiveDataSyncBrowserCache;

    /** The initial Y.Doc - only use for testing. */
    dangerouslySetInitialDoc?: Y.Doc;
}

const logger = Logger('hive:branch-document');

/**
 * Sync a Slate document with a Hive server.
 */
export class HiveWorkingDocument {
    public readonly contentId: string;
    public readonly branchId: string;
    public readonly containerType: 'pages' | 'reusable-contents';
    public readonly containerId: string;

    public selections: List<SelectionRange> = List.create();

    public completions: DataSyncRPCService<'DocumentCompletions'>;

    private readonly manager: HiveDataSyncManager;
    public readonly dataSync: HiveDataSyncClient<HiveDocumentPresence>;

    private events = new EventEmitter();
    private singleEditBatchingInterval: number;

    #value: Value;

    constructor(
        manager: HiveDataSyncManager,
        guid: string,
        params: {
            contentId: string;
            branchId: string;
            containerType: 'pages' | 'reusable-contents';
            containerId: string;
        },
        options: HiveWorkingDocumentOptions = {}
    ) {
        this.manager = manager;
        this.contentId = params.contentId;
        this.branchId = params.branchId;
        this.containerType = params.containerType;
        this.containerId = params.containerId;

        this.singleEditBatchingInterval =
            options.singleEditBatchingInterval ?? (process.env.NODE_ENV === 'test' ? 0 : 1500);

        this.#value = Value.create();

        this.dataSync = new HiveDataSyncClient(manager.client, {
            guid,
            contentId: this.contentId,
            path: '/branches/:branchId/:containerType/:containerId/document/datasync',
            pathParams: {
                branchId: this.branchId,
                containerType: this.containerType,
                containerId: this.containerId,
            },
            YType: Y.XmlElement,
            readOnly: options.readOnly,
            cache: options.cache,
            dangerouslySetInitialDoc: options.dangerouslySetInitialDoc,
        });
        this.dataSync.resetBatchingInterval(this.singleEditBatchingInterval);

        this.dataSync.listen('restorePositions', (positions) => {
            let selection = this.createSelectionFromRelatives(this.value.document, positions);
            selection = selection ? selection.normalize(this.value.document) : TextRange.create();

            if (this.#value.selection.isFocused) {
                selection = selection.focus();
            }
            this.#value = this.#value.merge({
                selection,
            });
            this.events.emit('value');
        });

        this.dataSync.listen('update', (origin: HiveDataSyncUpdateOrigin) => {
            if (origin === HiveDataSyncUpdateOrigin.LOCAL) {
                // .doc and .value should be in sync already
            } else {
                this.createSlateValue();
            }

            this.events.emit('value');
        });

        this.dataSync.listen('presences', () => {
            if (this.dataSync.presences.length > 0) {
                if (this.dataSync.hasBatchingEnabled()) {
                    this.dataSync.disableBatching();
                }
            } else {
                if (!this.dataSync.hasBatchingEnabled()) {
                    this.dataSync.resetBatchingInterval(this.singleEditBatchingInterval);
                }
            }

            this.updateSelections();
        });

        this.dataSync.listen('state', (state) => {
            if (state === HiveDataSyncState.OPEN) {
                this.updateSelections();
            }
        });

        const [completions] = this.dataSync.withRPC('DocumentCompletions');
        this.completions = completions;
    }

    /**
     * Stream AI completions for the current document.
     */
    async *streamRPCMethod<TReply, TRequest, TServiceName extends DataSyncRPCServiceName>(
        serviceName: TServiceName,
        methodName: keyof DataSyncRPCService<TServiceName>,
        request: TRequest
    ): AsyncIterable<TReply> {
        const [service, dispose] = this.dataSync.withRPC(serviceName);
        let replies: TReply[] = [];
        let resolve: () => void;
        let reject: (error: unknown) => void;

        // TODO: Promise.withResolvers
        let promise = new Promise<void>((r, re) => {
            resolve = r;
            reject = re;
        });
        let done = false;

        function next(reply: TReply) {
            replies.push(reply);
            resolve();

            // TODO: Promise.withResolvers
            promise = new Promise<void>((r, re) => {
                resolve = r;
                reject = re;
            });
        }

        function validateReply(response: unknown): asserts response is TReply {
            if (
                typeof response !== 'object' ||
                !response ||
                !('id' in response) ||
                !('completion' in response) ||
                typeof response.id !== 'string'
            ) {
                throw new Error('Invalid response');
            }
        }

        function handleData(reply: unknown) {
            validateReply(reply);
            next(reply);
        }

        function handleEnd() {
            dispose();
            done = true;
            resolve();
        }

        function handleError(error: unknown) {
            logger.error('Error while streaming RPC result', error);
            dispose();
            reject(error);
        }

        service.on('data', handleData);
        service.on('end', handleEnd);
        service.on('error', handleError);
        const method = service[methodName];
        if (typeof method !== 'function') {
            throw new Error(`Invalid method name: ${String(methodName)}`);
        }
        method
            .bind(service)(request)
            .catch(() => {
                // Error are handled with the 'error' event
            });
        while (!done) {
            await promise;
            yield* replies;
            replies = [];
        }
    }

    /**
     * Stream AI completions for the current document.
     */
    streamAICompletion(request: IAICompletionRequest): AsyncIterable<IAICompletionReply> {
        return this.streamRPCMethod('DocumentCompletions', 'streamAICompletion', request);
    }

    /**
     * Getter for the current value of the document.
     * It makes sure `this.#value` can't be modified outside of the class.
     */
    get value() {
        return this.#value;
    }

    /**
     * Convert the Yjs document into a complete Slate value.
     */
    private createSlateValue() {
        const yDocument = getYSlateDocument(this.dataSync.doc);

        this.dataSync.doc.transact(() => {
            return normalizeYDocument(yDocument);
        }, HiveDataSyncUpdateOrigin.LOCAL);

        const document = yToDocument(yDocument);

        this.dataSync.doc.transact(() => {
            const previousSelection = this.value.selection;
            const schema = SCHEMAS[document.data.get('schemaType') || 'document'];

            let value = Value.create(
                {
                    document,
                },
                {
                    normalize: false,
                }
            ).setSchema(schema);

            if (value.document !== document) {
                // We run this check to absolutely make sure that we manage any form of normalization
                // below to sync with the other clients and not create a mismatch between `value` and `this.dataSync.doc`.
                throw new Error('Document should not be modified by creation of Value');
            }

            // Normalize the document and sync it with the server if it changed
            const normalization = value.change({ save: false }).normalize();
            if (normalization.value.document !== value.document) {
                value = value.merge({
                    document: normalization.value.document,
                });

                documentChangeToY(normalization, yDocument);
                const getExecutionTime = startStopwatch();

                try {
                    // We've seen issues where, after documentChangeToY, the Yjs doc does not match the passed Slate doc.
                    checkYDocument(normalization.value.document, yDocument);
                } catch (error) {
                    if (!(error instanceof YDocumentCheckFail)) {
                        throw error;
                    }

                    const durationMs = getExecutionTime();

                    // It'll crash the app but it prevents potentially invalid Yjs documents from being synced to the server.
                    this.dataSync.destroy();

                    // Report the error in Sentry along with the operations in the change.
                    throw ReportException.annotateError(error, {
                        tags: {
                            [SentryTags.ERROR_CATEGORY]: 'hive-document-init',
                        },
                        extra: {
                            durationMs,
                            contentId: this.contentId,
                            branchId: this.branchId,
                            containerType: this.containerType,
                            containerId: this.containerId,
                        },
                        attachments: [
                            {
                                name: 'normalization.json',
                                body: serializeChange(error, normalization),
                            },
                            {
                                name: 'updates.bin',
                                body: this.dataSync.getUpdateLog(),
                            },
                        ],
                    });
                }
            }

            // Create a selection based on the relative position of the previous one
            // It ensures the cursor is where the user expects it to be when someone is typing before/between.
            let selection = this.dataSync.presence
                ? this.createSelectionFromRelatives(
                      value.document,
                      this.dataSync.presence.positions
                  )
                : undefined;
            if (selection) {
                selection = selection.normalize(document);
            }
            if (selection && previousSelection.isFocused) {
                selection = selection.focus();
            }

            value = value.merge({
                selection: selection ?? TextRange.create(),
            });

            // Set the value before finishing the transaction. It means anything listening to
            // updates on the doc will see the new value.
            this.#value = value;
        }, HiveDataSyncUpdateOrigin.LOCAL);
    }

    /**
     * Prevent the manager from closing this sync before the cleanup callback is called.
     */
    public keepAlive() {
        return this.manager.keepAlive(this);
    }

    /**
     * Connect to the backend of the document.
     */
    public open(): void | Promise<void> {
        return this.dataSync.open(
            this.manager.clientId
                ? {
                      clientId: this.manager.clientId,
                  }
                : {}
        );
    }

    /**
     * Close the connection to the branch and garbage collect it.
     */
    public close() {
        this.manager.close(this);
    }

    /**
     * Destroy the connection. It cannot be re-opened later and needs to be recreated.
     */
    public destroy() {
        this.dataSync.destroy();
    }

    /**
     * Wait for all changes to be synced.
     */
    public waitForSync() {
        return this.dataSync.waitForSync();
    }

    /**
     * Add a callback to be called on certain events.
     *  - 'change': the document has changed
     *  - 'selections': the selections have changed
     */
    public listen(event: 'value' | 'selections', callback: () => void): () => void {
        this.events.addListener(event, callback);

        return () => {
            this.events.removeListener(event, callback);
        };
    }

    /**
     * Apply a change to the synced value.
     */
    public applyChange(change: Change): void {
        if (this.dataSync.readOnly) {
            throw new Error(`can't apply change when dataSync is in read-only mode`);
        }

        this.manager.touch(this);

        const previousValue = this.value;

        if (previousValue !== change.initialValue) {
            const error = new YDocumentCheckFail(
                'Slate change.initialValue does not match the value of HiveWorkingDocument. This could indicate a nested .change() call, or a datasync inconsistency issue.'
            );

            // Destroy the datasync and crash.
            this.dataSync.destroy();

            // The Change was not created with the correct Yjs doc.
            // Report the error in Sentry along with the operations in the change.
            throw ReportException.annotateError(error, {
                tags: {
                    [SentryTags.ERROR_CATEGORY]: 'hive-document-initial-value',
                },
                extra: {
                    contentId: this.contentId,
                    branchId: this.branchId,
                    containerType: this.containerType,
                    containerId: this.containerId,
                },
                attachments: [
                    {
                        name: 'change.json',
                        body: serializeChange(error, change),
                    },
                    {
                        name: 'updates.bin',
                        body: this.dataSync.getUpdateLog(),
                    },
                ],
            });
        }

        this.#value = change.value;

        if (previousValue.document !== change.value.document) {
            const getExecutionTime = startStopwatch();

            this.dataSync.transact((doc) => {
                // Modify the Yjs doc using the new slate value.
                documentChangeToY(change, getYSlateDocument(doc));

                try {
                    // We've seen issues where, after documentChangeToY, the Yjs doc does not match the passed Slate doc.
                    // e.g. https://linear.app/gitbook-x/issue/SUP-737/git-sync-export-fail-cannot-read-properties-of-undefined
                    // We are trying to find the root cause by checking the Yjs doc against the Slate doc and reporting if they don't match.
                    checkYDocument(change.value.document, getYSlateDocument(doc));
                } catch (error) {
                    if (!(error instanceof YDocumentCheckFail)) {
                        throw error;
                    }

                    const durationMs = getExecutionTime();

                    // Revert the value and stop everything
                    this.#value = previousValue;
                    this.dataSync.destroy();

                    // Report the error in Sentry along with the operations in the change.
                    throw ReportException.annotateError(error, {
                        tags: {
                            [SentryTags.ERROR_CATEGORY]: 'hive-document-change',
                        },
                        extra: {
                            durationMs,
                            contentId: this.contentId,
                            branchId: this.branchId,
                            containerType: this.containerType,
                            containerId: this.containerId,
                        },
                        attachments: [
                            {
                                name: 'change.json',
                                body: serializeChange(error, change),
                            },
                            {
                                name: 'updates.bin',
                                body: this.dataSync.getUpdateLog(),
                            },
                        ],
                    });
                }
            });

            // When document is changed locally, we also update the selections to best position them
            // in the new document.
            this.updateSelections();
        } else {
            // Emit the event to indicate that the value changed
            // as it'll not be emitted by the `update` event of the data sync client.
            this.events.emit('value');
        }

        if (change.value.selection !== previousValue.selection) {
            const selection = change.value.selection;

            this.dataSync.setPresence({
                data: {
                    ...(this.dataSync.presence?.data || {}),
                },
                positions: selectionToYSelection(
                    change.value.document,
                    this.dataSync.doc,
                    selection
                ),
            });
        }

        if (change.value.selection.isBlurred) {
            // When user is blurring the editor, we flush all updates to the server
            // in case they are blurring it to trigger a side effect that might depend on the updates
            // (e.g. merging the branch).
            void this.dataSync.flushBatchUpdates();
        }
    }

    /**
     * Make a change to the synced value.
     */
    public async change(callback: (change: Change) => void): Promise<void> {
        const change = this.value.change();
        callback(change);
        this.applyChange(change);

        await this.waitForSync();
    }

    /**
     * Update the list of selections from the map of presences.
     */
    private updateSelections() {
        if (this.dataSync.state !== HiveDataSyncState.OPEN) {
            // Don't update selections if we're not connected
            // as this.dataSync.doc might not be available as a representation of the document.
            this.selections = List.create();
        } else {
            this.selections = List.create(
                this.dataSync.presences
                    .map((presence) => {
                        const selection = this.createSelectionFromRelatives(
                            this.value.document,
                            presence.positions
                        );
                        if (!selection) {
                            return null;
                        }

                        return selection.merge({
                            data: Data.create({
                                clientId: presence.clientId,
                                ...presence.data,
                            }),
                        });
                    })
                    .filter(filterOutNullable)
            );
        }

        this.events.emit('selections');
    }

    /**
     * Undo the last change.
     */
    public undo() {
        return this.dataSync.undo();
    }

    /**
     * Redo the last undoed change.
     */
    public redo() {
        return this.dataSync.redo();
    }

    /**
     * Create a Slate selection from a selection composed of Yjs relative positions.
     */
    private createSelectionFromRelatives(
        document: Document,
        position: Record<string, Y.RelativePosition>
    ): SelectionRange | undefined {
        const doc = this.dataSync.doc;

        return ySelectionToSelection(document, {
            anchor: position.anchor
                ? Y.createAbsolutePositionFromRelativePosition(position.anchor, doc) || undefined
                : undefined,
            focus: position.focus
                ? Y.createAbsolutePositionFromRelativePosition(position.focus, doc) || undefined
                : undefined,
        });
    }
}

/**
 * Serialize an error-causing Change to be sent to Sentry.
 */
function serializeChange(error: YDocumentCheckFail, change: Change): string {
    return JSON.stringify({
        initialValue: serialize(change.initialValue),
        ...(error.slateValue ? { slateValue: error.slateValue } : {}),
        ...(error.yValue ? { yValue: error.yValue } : {}),
        operations: change.operations.map(([operation]) => serialize(operation)),
        flags: Object.keys(change.flags).length > 0 ? change.flags : undefined,
    });
}
